/**
 * Console d'enrichissement d'une fiche : d'où viennent les contacts, et ce que
 * chaque passage a tenté — trouvé, rien trouvé, accès refusé par le site,
 * page absente — avec les liens pour aller vérifier à la source.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, MinusCircle, ShieldAlert, AlertTriangle, SkipForward, Clock, ExternalLink, Phone, Mail, Globe, RefreshCw } from "lucide-react";
import api from "@/lib/crmApi";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

const STATUS_META = {
  found: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  nothing: { icon: MinusCircle, cls: "text-slate-500 bg-slate-50 border-slate-200" },
  blocked: { icon: ShieldAlert, cls: "text-rose-600 bg-rose-50 border-rose-200" },
  missing: { icon: MinusCircle, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  timeout: { icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  error: { icon: AlertTriangle, cls: "text-rose-600 bg-rose-50 border-rose-200" },
  skipped: { icon: SkipForward, cls: "text-slate-400 bg-slate-50 border-slate-200" },
  rate_limited: { icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  budget: { icon: Clock, cls: "text-amber-700 bg-amber-50 border-amber-200" },
};
const FIELD_LABEL = { telephone: "Téléphone", telephone_secondaire: "Tél. secondaire", email: "Email", site_web: "Site web",
  adresse: "Adresse", code_postal: "Code postal", ville: "Ville", latitude: "Latitude", longitude: "Longitude", nom_commercial: "Enseigne" };
const CONTACTS = [["telephone", "Téléphone", Phone], ["email", "Email", Mail], ["site_web", "Site web", Globe]];

/** Lien « voir la source » — ou rien si la source n'a pas de page. */
export function SourceLink({ url, label, compact = false }) {
  if (!url) return label ? <span className="text-[11.5px] text-slate-400">via {label}</span> : null;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="source-link" title={url}
      className="inline-flex items-center gap-1 text-[11.5px] text-indigo-600 hover:underline">
      {compact ? "source" : `via ${label || "source"}`} <ExternalLink size={11} />
    </a>
  );
}

export default function EnrichmentConsole({ company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => {
    setLoading(true);
    api.get(`/enrichment/companies/${company.id}/sources`)
      .then(({ data: d }) => setData(d)).catch(() => setData({ runs: [], provenance: {} }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) return <p className="text-sm text-slate-400">Chargement…</p>;
  const prov = data?.provenance || {};
  const runs = data?.runs || [];

  return (
    <div data-testid="enrichment-console">
      <div className="mb-4 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provenance des contacts</h4>
        <button onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800"><RefreshCw size={12} /> Actualiser</button>
      </div>
      <div className="mb-6 grid gap-2 sm:grid-cols-3" data-testid="provenance-cards">
        {CONTACTS.map(([field, label, Icon]) => {
          const p = prov[field];
          const value = company[field];
          return (
            <div key={field} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`provenance-${field}`}>
              <div className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400"><Icon size={12} /> {label}</div>
              <div className="truncate text-[13px] text-slate-800">{value || <span className="text-slate-300">—</span>}</div>
              <div className="mt-1 text-[11.5px]">
                {p ? (
                  <>
                    <span className="text-slate-500">{p.provider_label}{p.date && ` · ${fmt(p.date)}`}</span>
                    <div><SourceLink url={p.source_url} label={p.provider_label} compact /></div>
                  </>
                ) : value ? <span className="text-slate-400">saisi à la main ou importé — pas de source enregistrée</span>
                  : <span className="text-slate-400">non renseigné</span>}
              </div>
            </div>
          );
        })}
      </div>

      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Console des enrichissements</h4>
      {runs.length === 0 ? (
        <p className="text-sm text-slate-400">Aucun enrichissement tracé sur cette fiche. Les passages antérieurs à la console n'y figurent pas.</p>
      ) : (
        <div className="space-y-3">
          {runs.map((run) => <RunCard key={run.id} run={run} />)}
        </div>
      )}
    </div>
  );
}

export function RunCard({ run, defaultOpen = true, showCompany = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-xl border bg-white ${run.has_problem ? "border-rose-200" : "border-slate-200"}`} data-testid="enrichment-run">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full flex-wrap items-center gap-2 px-3.5 py-2.5 text-left hover:bg-slate-50/70">
        <span className="font-mono text-[12px] text-slate-500">{fmt(run.started_at)}</span>
        {showCompany && run.company_id && (
          <a href={`/entreprises/${run.company_id}?onglet=sources`} onClick={(e) => e.stopPropagation()}
            className="text-[13px] font-medium text-indigo-700 hover:underline" data-testid="run-company">
            {run.company_name || "Fiche"}
          </a>
        )}
        {run.has_problem && <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase text-rose-700">problème</span>}
        <span className={`rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold uppercase ${run.mode === "ai" ? "bg-violet-50 text-violet-700" : "bg-indigo-50 text-indigo-700"}`}>
          {run.mode === "ai" ? "IA" : "classique"}
        </span>
        <span className="text-[12.5px] text-slate-600">{run.summary || `${(run.steps || []).length} sources`}</span>
        {(run.applied || []).length > 0 && (
          <span className="ml-auto text-[12px] text-emerald-700">{run.applied.length} champ{run.applied.length > 1 ? "s" : ""} écrit{run.applied.length > 1 ? "s" : ""}</span>
        )}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-3.5 py-2.5">
          <ol className="space-y-1.5">
            {(run.steps || []).map((s, i) => {
              const meta = STATUS_META[s.status] || STATUS_META.nothing;
              const Icon = meta.icon;
              return (
                <li key={`${s.provider}-${i}`} className="flex flex-wrap items-start gap-2 text-[12.5px]" data-testid={`step-${s.provider}`} data-status={s.status}>
                  <span className={`mt-px inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${meta.cls}`}>
                    <Icon size={11} /> {s.status_label || s.status}
                  </span>
                  <span className="font-medium text-slate-700">{s.label || s.provider}</span>
                  {s.status === "found" && (
                    <span className="text-slate-500">
                      {s.evidences} observation{s.evidences > 1 ? "s" : ""}{s.fields?.length ? ` : ${s.fields.map((f) => FIELD_LABEL[f] || f).join(", ")}` : ""}
                    </span>
                  )}
                  {s.error && <span className="text-slate-500">{s.error}</span>}
                  {(s.source_urls || []).slice(0, 2).map((u) => <SourceLink key={u} url={u} compact />)}
                  {(s.notes || []).length > 0 && (
                    <ul className="w-full pl-6 text-[12px] text-slate-500">
                      {s.notes.map((n, j) => (
                        <li key={j} className={n.kind === "blocked" ? "text-rose-600" : ""}>
                          · {n.message} {n.url && <SourceLink url={n.url} compact />}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
          {(run.applied || []).length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2 text-[12px] text-slate-600">
              Écrit dans la fiche : {run.applied.map((a, i) => (
                <span key={i} className="mr-2 inline-flex items-center gap-1">
                  <b>{FIELD_LABEL[a.field] || a.field}</b> = {a.value} <SourceLink url={a.source_url} compact />
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
